import { useEffect, useMemo, useState } from 'react';
import { Edit2, MoreVertical, Settings, Bell, CheckCircle2, Clock, XCircle, Send, Mail } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { NoGroupsMessage } from '../components/NoGroupsMessage';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { apiClient } from '../lib/api-client';
import { Badge } from '../components/ui/Badge';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { Button } from '../components/ui/Button';
import { getTenantColor } from '../lib/colors';
import { TenantLogo } from '../components/tenants/TenantLogo';
import { NotificationChannelsDialog } from '../components/notifications/NotificationChannelsDialog';
import { TenantNotificationConfigDialog } from '../components/notifications/TenantNotificationConfigDialog';
import { GraphApiRequestDialog } from '../components/notifications/GraphApiRequestDialog';
import { SentEmailsDialog } from '../components/notifications/SentEmailsDialog';
import { Skeleton } from '../components/ui/Skeleton';
import { PageHeader } from '../components/ui/PageHeader';

interface GraphApiStatus {
  accessGranted: boolean;
  grantedAt?: string;
  grantedBy?: string;
  pendingRequest: boolean;
  requestedAt?: string;
  requestedBy?: string;
  adminEmail?: string;
}

export function Notifications() {
  const { user, accessibleTenants, selectedTenants } = useAuth();
  const { isAllowed: canView } = usePermission('notifications', 'read');
  const { isAllowed: canManage } = usePermission('notifications', 'manage');
  const { isAllowed: canRequestGraphApi } = usePermission('notifications', 'request_graph_api');
  const { isAllowed: canApproveGraphApi } = usePermission('notifications', 'approve_graph_api');

  const hasNoGroups = user && (!user.groups || user.groups.length === 0);
  if (hasNoGroups) return <NoGroupsMessage />;

  const tenantsToQuery = useMemo(() => {
    const list = (selectedTenants && selectedTenants.length > 0) ? selectedTenants : accessibleTenants;
    return (list || []).map((t) => String(t).toLowerCase());
  }, [accessibleTenants, selectedTenants]);

  const [tenants, setTenants] = useState<Array<{ code: string; name: string; color?: string | null; domains?: string[] }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [showMasterEditor, setShowMasterEditor] = useState(false);
  const [showSentEmails, setShowSentEmails] = useState(false);
  const [graphApiStatuses, setGraphApiStatuses] = useState<Record<string, GraphApiStatus>>({});
  const [tenantEditorTenant, setTenantEditorTenant] = useState<string | null>(null);
  const [graphApiDialogTenant, setGraphApiDialogTenant] = useState<{
    code: string;
    name: string;
    domains?: string[];
    status?: GraphApiStatus;
  } | null>(null);
  const [teamsInboxAuth, setTeamsInboxAuth] = useState<any | null>(null);
  const [teamsInboxLoading, setTeamsInboxLoading] = useState(false);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setActionMsg(null);
    void (async () => {
      try {
        const tenantsResponse = await apiClient.getTenants();
        const tenantsArr = (Array.isArray(tenantsResponse) ? tenantsResponse : tenantsResponse.tenants || []) as any[];
        const wanted = new Set((tenantsToQuery || []).map((t) => String(t).toLowerCase()));
        const filtered = tenantsArr
          .map((t: any) => ({
            code: String(t.code || '').toLowerCase(),
            name: t.name,
            color: t.color,
            logoUrl: t.logoUrl,
            domains: t.domains || [],
          }))
          .filter((t: any) => wanted.has(String(t.code).toLowerCase()));
        if (!cancelled) setTenants(filtered);

        // Load Graph API statuses
        const statusesRes = await apiClient.getTenantsGraphApiStatus();
        if (!cancelled && statusesRes?.statuses) {
          setGraphApiStatuses(statusesRes.statuses);
        }
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load notifications config');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canView, tenantsToQuery.join(',')]);

  useEffect(() => {
    if (!canManage) return;
    let cancelled = false;
    setTeamsInboxLoading(true);
    void (async () => {
      try {
        const status = await apiClient.getTeamsInboxAuthStatus();
        if (!cancelled) setTeamsInboxAuth(status);
      } catch {
        // ignore
      } finally {
        if (!cancelled) setTeamsInboxLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canManage]);

  const openTeamsInboxAuth = () => {
    const rd = `${window.location.origin}/notifications`;
    window.open(`/api/azure-auth/login/teams-inbox?rd=${encodeURIComponent(rd)}`, '_blank', 'noopener,noreferrer');
  };

  const refreshTeamsInboxAuth = async () => {
    setTeamsInboxLoading(true);
    try {
      const status = await apiClient.getTeamsInboxAuthStatus();
      setTeamsInboxAuth(status);
    } catch {
      // ignore
    } finally {
      setTeamsInboxLoading(false);
    }
  };

  const refreshGraphApiStatuses = async () => {
    try {
      const statusesRes = await apiClient.getTenantsGraphApiStatus();
      if (statusesRes?.statuses) {
        setGraphApiStatuses(statusesRes.statuses);
      }
    } catch {
      // ignore
    }
  };

  if (!canView) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notifications</CardTitle>
        </CardHeader>
        <CardContent>You don&apos;t have permission to view Notifications.</CardContent>
      </Card>
    );
  }

  const getGraphApiStatusBadge = (status?: GraphApiStatus) => {
    if (status?.accessGranted) {
      return (
        <Badge variant="success" className="flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" />
          Graph API
        </Badge>
      );
    }
    if (status?.pendingRequest) {
      return (
        <Badge variant="warning" className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Pending
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="flex items-center gap-1 text-gray-500">
        <XCircle className="w-3 h-3" />
        No Access
      </Badge>
    );
  };

  return (
    <div className="space-y-4">
      <PageHeader
        icon={Bell}
        iconColor="text-yellow-600 dark:text-yellow-400"
        iconBgColor="bg-yellow-100 dark:bg-yellow-900/30"
        description="Manage notification channels and Graph API access for Teams notifications"
        count={tenants.length}
        countLabel="tenants"
        countColor="text-yellow-600 dark:text-yellow-400"
        actions={
          canManage ? (
            <div className="w-full sm:w-auto sm:h-full flex items-stretch">
              <DropdownMenu
                className="w-full h-full"
                trigger={
                  <Button variant="outline" size="sm" className="w-full h-full text-sm py-2">
                    <Settings className="w-4 h-4 mr-2" />
                    Actions
                  </Button>
                }
                items={[
                  {
                    label: 'Edit Master Notification Channels',
                    icon: <Edit2 className="w-4 h-4" />,
                    onClick: () => setShowMasterEditor(true),
                  },
                  {
                    label: 'View Sent Emails',
                    icon: <Mail className="w-4 h-4" />,
                    onClick: () => setShowSentEmails(true),
                  },
                ]}
              />
            </div>
          ) : undefined
        }
      />

      {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}
      {actionMsg && <div className="text-sm text-green-700 dark:text-green-400">{actionMsg}</div>}

      {canManage && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Teams Inbox (Service Account)
            </CardTitle>
            <CardDescription>
              Used to send portal notifications into Teams chats. Requires delegated authorization.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <div className="text-sm text-gray-600 dark:text-gray-400">Inbox user</div>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {teamsInboxLoading ? <Skeleton className="h-4 w-56" /> : (teamsInboxAuth?.inboxEmail || 'Not configured')}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600 dark:text-gray-400">Authorization status</div>
                {teamsInboxLoading ? (
                  <Skeleton className="h-6 w-24" />
                ) : (
                  <Badge
                    variant={
                      teamsInboxAuth?.health === 'healthy'
                        ? 'success'
                        : teamsInboxAuth?.health === 'invalid'
                          ? 'warning'
                          : 'outline'
                    }
                  >
                    {teamsInboxAuth?.health === 'healthy'
                      ? 'Healthy'
                      : teamsInboxAuth?.health === 'invalid'
                        ? 'Re-auth required'
                        : 'Not authorized'}
                  </Badge>
                )}
              </div>

              {!teamsInboxLoading && teamsInboxAuth?.lastFailureReason && (
                <div className="text-xs text-amber-700 dark:text-amber-400">
                  {teamsInboxAuth.lastFailureReason}
                </div>
              )}

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Button size="sm" onClick={openTeamsInboxAuth} disabled={teamsInboxLoading || !teamsInboxAuth?.inboxEmail}>
                  Authorize Inbox User
                </Button>
                <Button variant="outline" size="sm" onClick={refreshTeamsInboxAuth} disabled={teamsInboxLoading}>
                  Refresh Status
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <Skeleton variant="circle" className="w-10 h-10" />
                    <div className="space-y-2">
                      <Skeleton className="h-5 w-24" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {tenants.length === 0 && !loading && !error ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500 dark:text-gray-400">
            No tenants found for current selection.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {tenants.map((tenant) => {
            const graphApiStatus = graphApiStatuses[tenant.code];

            const menuItems = [];

            if (canManage) {
              menuItems.push({
                label: 'Edit Notification Config',
                icon: <Edit2 className="w-4 h-4" />,
                onClick: () => setTenantEditorTenant(tenant.code),
              });
            }

            if (canRequestGraphApi && !graphApiStatus?.accessGranted) {
              menuItems.push({
                label: graphApiStatus?.pendingRequest ? 'View Request Status' : 'Request Graph API Access',
                icon: <Send className="w-4 h-4" />,
                onClick: () => setGraphApiDialogTenant({
                  code: tenant.code,
                  name: tenant.name,
                  domains: tenant.domains || [],
                  status: graphApiStatus,
                }),
              });
            }

            if (canApproveGraphApi && graphApiStatus?.pendingRequest) {
              menuItems.push({
                label: 'Approve Graph API Access',
                icon: <CheckCircle2 className="w-4 h-4" />,
                onClick: () => setGraphApiDialogTenant({
                  code: tenant.code,
                  name: tenant.name,
                  domains: tenant.domains || [],
                  status: graphApiStatus,
                }),
              });
            }

            if (graphApiStatus?.accessGranted) {
              menuItems.push({
                label: 'View Graph API Status',
                icon: <CheckCircle2 className="w-4 h-4" />,
                onClick: () => setGraphApiDialogTenant({
                  code: tenant.code,
                  name: tenant.name,
                  domains: tenant.domains || [],
                  status: graphApiStatus,
                }),
              });
            }

            return (
              <Card key={tenant.code} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <TenantLogo tenant={tenant} size="md" rounded="lg" className="border border-gray-200/80 dark:border-gray-700/80" />
                      <div>
                        <CardTitle className="text-lg" style={{ color: getTenantColor(tenant) }}>
                          {tenant.name}
                        </CardTitle>
                        <CardDescription className="font-mono text-xs mt-1">
                          {tenant.code}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      {menuItems.length > 0 && (
                        <DropdownMenu
                          trigger={
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          }
                          items={menuItems}
                        />
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Graph API Access</span>
                      {getGraphApiStatusBadge(graphApiStatus)}
                    </div>
                    
                    {graphApiStatus?.accessGranted && graphApiStatus.grantedAt && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Granted {new Date(graphApiStatus.grantedAt).toLocaleDateString()}
                      </p>
                    )}

                    {graphApiStatus?.pendingRequest && graphApiStatus.requestedAt && (
                      <p className="text-xs text-amber-600 dark:text-amber-400">
                        Requested {new Date(graphApiStatus.requestedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Master Notification Channels Dialog */}
      <NotificationChannelsDialog
        open={showMasterEditor}
        onClose={() => setShowMasterEditor(false)}
        onSuccess={() => {
          setActionMsg('Master notification channels updated');
          setTimeout(() => setActionMsg(null), 3000);
        }}
      />

      {/* Tenant Notification Config Dialog */}
      <TenantNotificationConfigDialog
        open={!!tenantEditorTenant}
        tenantCode={tenantEditorTenant}
        onClose={() => setTenantEditorTenant(null)}
        onSuccess={() => {
          setActionMsg('Tenant notification config updated');
          setTimeout(() => setActionMsg(null), 3000);
        }}
      />

      {/* Graph API Request Dialog */}
      <GraphApiRequestDialog
        open={!!graphApiDialogTenant}
        onClose={() => setGraphApiDialogTenant(null)}
        onSuccess={() => {
          refreshGraphApiStatuses();
          setActionMsg('Graph API status updated');
          setTimeout(() => setActionMsg(null), 3000);
        }}
        tenantCode={graphApiDialogTenant?.code || null}
        tenantName={graphApiDialogTenant?.name}
        tenantDomains={graphApiDialogTenant?.domains || []}
        currentStatus={graphApiDialogTenant?.status}
        canApprove={canApproveGraphApi}
      />

      {/* Sent Emails Dialog */}
      <SentEmailsDialog
        open={showSentEmails}
        onClose={() => setShowSentEmails(false)}
      />
    </div>
  );
}
